import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../Shared/Services/api.service';
import { endpoints } from '../../../../environments/endpoints';
import { LaravelDefaultResponse } from '../../../Interfaces/api/shared/response.interface';
import {
  OrganizationNoteListResponse,
  OrganizationNoteShowResponse,
} from '../../../Interfaces/api/backoffice/organization-note.interface';
import { TypedResourceRequest } from '../../Shared/Interfaces/api-request.interface';
import { OrganizationNote } from '../../../Models/backoffice/organization-note.model';

@Injectable({
  providedIn: 'root',
})
export class OrganizationNoteService {
  private readonly _apiService = inject(ApiService);

  listRequest(
    organizationId: string,
  ): TypedResourceRequest<LaravelDefaultResponse<OrganizationNoteListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<OrganizationNoteListResponse>>(
      endpoints.backoffice.organizationNotes(organizationId),
      { skipLoading: true },
    );
  }

  create(
    organizationId: string,
    body: string,
    pinned: boolean,
  ): Observable<OrganizationNote | null> {
    const request = this._apiService.post<LaravelDefaultResponse<OrganizationNoteShowResponse>>(
      endpoints.backoffice.organizationNotes(organizationId),
      { body: { body, pinned }, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<OrganizationNoteShowResponse>>(request)
      .pipe(map((response) => this._toNote(response)));
  }

  update(
    organizationId: string,
    noteId: string,
    body: string,
    pinned: boolean,
  ): Observable<OrganizationNote | null> {
    const request = this._apiService.put<LaravelDefaultResponse<OrganizationNoteShowResponse>>(
      endpoints.backoffice.organizationNote(organizationId, noteId),
      { body: { body, pinned }, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<OrganizationNoteShowResponse>>(request)
      .pipe(map((response) => this._toNote(response)));
  }

  setPinned(
    organizationId: string,
    noteId: string,
    pinned: boolean,
  ): Observable<OrganizationNote | null> {
    const request = this._apiService.put<LaravelDefaultResponse<OrganizationNoteShowResponse>>(
      endpoints.backoffice.organizationNote(organizationId, noteId),
      { body: { pinned }, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<OrganizationNoteShowResponse>>(request)
      .pipe(map((response) => this._toNote(response)));
  }

  remove(organizationId: string, noteId: string): Observable<void> {
    const request = this._apiService.delete<LaravelDefaultResponse<null>>(
      endpoints.backoffice.organizationNote(organizationId, noteId),
      { skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<null>>(request)
      .pipe(map(() => undefined as void));
  }

  toNotes(
    response: LaravelDefaultResponse<OrganizationNoteListResponse> | undefined,
  ): OrganizationNote[] {
    return (response?.data?.notes ?? []).map((row) => new OrganizationNote(row));
  }

  private _toNote(
    response: LaravelDefaultResponse<OrganizationNoteShowResponse>,
  ): OrganizationNote | null {
    return response.data?.note ? new OrganizationNote(response.data.note) : null;
  }
}
